import { useMemo, useState } from "react";
import { Link } from "react-router";
import { Aviso, Botao, CampoLongo, Cartao, Chip } from "../ui";
import {
  descreverMudanca,
  useAplicarMacro,
  useMacros,
  usePreviaDaMacro,
  type Macro,
  type PreviaDaMacro,
} from "../../nucleo/automacao";
import { mensagemDoConsole } from "../../nucleo/console";
import { podeNaSessao, useSessao } from "../../nucleo/sessao";
import { useAgentesDeAtendimento, useEquipesDeAtendimento } from "../../nucleo/tickets";

/**
 * As macros no chamado interno: um clique traz a resposta já com os marcadores preenchidos e
 * mostra o que mudará no chamado; nada é gravado até "Aplicar macro". O texto pode ser ajustado
 * à mão antes — a macro é um ponto de partida, não uma mensagem que sai sem ser lida.
 */
export function MacrosDoChamado({ ticketId }: { ticketId: string }) {
  const sessao = useSessao();
  const gerencia = podeNaSessao(sessao.data, "ticket:gerenciar");
  const administra = podeNaSessao(sessao.data, "automacao:gerenciar");
  const macros = useMacros();
  const equipes = useEquipesDeAtendimento(gerencia);
  const agentes = useAgentesDeAtendimento(gerencia);
  const previa = usePreviaDaMacro();
  const aplicar = useAplicarMacro();
  const [escolhida, setEscolhida] = useState<Macro | null>(null);
  const [rascunho, setRascunho] = useState<PreviaDaMacro | null>(null);
  const [texto, setTexto] = useState("");
  const [aplicada, setAplicada] = useState(false);

  const nomes = useMemo(() => {
    const mapa: Record<string, string> = {};
    for (const equipe of equipes.data ?? []) mapa[equipe.id] = equipe.nome;
    for (const agente of agentes.data ?? []) mapa[agente.id] = agente.nome;
    return mapa;
  }, [equipes.data, agentes.data]);

  function escolher(macro: Macro) {
    setAplicada(false);
    aplicar.reset();
    setEscolhida(macro);
    previa.mutate(
      { macroId: macro.id, ticketId },
      {
        onSuccess: (resultado) => {
          setRascunho(resultado);
          setTexto(resultado.texto ?? "");
        },
        onError: () => setRascunho(null),
      },
    );
  }

  function cancelar() {
    setEscolhida(null);
    setRascunho(null);
    setTexto("");
    previa.reset();
    aplicar.reset();
  }

  function confirmar() {
    if (!escolhida || !rascunho) return;
    aplicar.mutate(
      { macroId: escolhida.id, ticketId, texto: rascunho.texto !== null ? texto.trim() : null },
      {
        onSuccess: () => {
          setAplicada(true);
          setEscolhida(null);
          setRascunho(null);
          setTexto("");
        },
      },
    );
  }

  const semTexto = rascunho !== null && rascunho.texto === null;
  const textoVazio = rascunho !== null && rascunho.texto !== null && !texto.trim();

  return (
    <Cartao
      titulo="Macros"
      className="mb-4"
      acessorio={
        administra ? (
          <Link className="nota-rodape" to="/admin/automacao">
            Gerenciar macros
          </Link>
        ) : undefined
      }
    >
      <div data-testid="macros-do-chamado">
        {macros.isPending ? <p role="status">Carregando macros…</p> : null}
        {macros.isError ? <Aviso tom="erro">{mensagemDoConsole(macros.error)}</Aviso> : null}
        {macros.data && macros.data.length === 0 ? (
          <p className="nota-rodape">
            Você ainda não tem macros. Crie uma em Automação para responder com um clique.
          </p>
        ) : null}
        <div className="flex gap-2 flex-wrap" data-testid="lista-de-macros-do-chamado">
          {macros.data?.map((macro) => (
            <Botao
              key={macro.id}
              type="button"
              variante={escolhida?.id === macro.id ? "solido" : "fantasma"}
              data-testid={`macro-${macro.nome}`}
              onClick={() => escolher(macro)}
              disabled={previa.isPending || aplicar.isPending}
            >
              {macro.nome}
              {macro.compartilhada ? " · Compartilhada" : ""}
            </Botao>
          ))}
        </div>

        {previa.isPending ? <p role="status">Preparando a macro…</p> : null}
        {previa.isError ? (
          <Aviso tom="erro" className="mt-3">
            {mensagemDoConsole(previa.error)}
          </Aviso>
        ) : null}
        {aplicada ? (
          <Aviso tom="sucesso" className="mt-3" data-testid="macro-aplicada">
            Macro aplicada ao chamado.
          </Aviso>
        ) : null}

        {escolhida && rascunho ? (
          <div className="mt-3" data-testid="previa-da-macro">
            <p className="nota-rodape mb-2">
              {escolhida.nome} ·{" "}
              {rascunho.tipo_de_resposta === "interna" ? "vira nota interna" : "resposta pública ao cliente"}
            </p>
            {!semTexto ? (
              <CampoLongo
                rotulo="Texto da macro"
                ajuda="Ajuste o que quiser antes de aplicar."
                value={texto}
                onChange={(evento) => setTexto(evento.target.value)}
              />
            ) : null}
            {rascunho.mudancas.length > 0 ? (
              <div data-testid="mudancas-da-macro">
                <p className="campo__rotulo">Vai mudar no chamado</p>
                <ul>
                  {rascunho.mudancas.map((mudanca) => (
                    <li key={mudanca.campo}>{descreverMudanca(mudanca, nomes)}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="nota-rodape mb-2">Nenhum campo do chamado será alterado.</p>
            )}
            {aplicar.isError ? (
              <Aviso tom="erro" className="mb-3">
                {mensagemDoConsole(aplicar.error)}
              </Aviso>
            ) : null}
            <div className="flex gap-2">
              <Botao
                type="button"
                data-testid="aplicar-macro"
                onClick={confirmar}
                disabled={aplicar.isPending || textoVazio}
              >
                {aplicar.isPending ? "Aplicando…" : "Aplicar macro"}
              </Botao>
              <Botao type="button" variante="fantasma" onClick={cancelar}>
                Cancelar
              </Botao>
            </div>
          </div>
        ) : null}
      </div>
      {escolhida?.compartilhada ? <Chip estado="neutro">Macro compartilhada</Chip> : null}
    </Cartao>
  );
}
